import type { Content } from '@/modules/Entertainment/interface/Content';
import type { FC, MouseEvent } from 'react';
import Link from 'next/link';
import { Box, Flex, Text, type FlexProps } from '@eqi-investimentos/react';
import {
  Button,
  Image,
  Progress,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react';
import { PATHS } from '@/config/paths';
import { CardFlipHorizontal } from '@/components/elements/CardFlipHorizontal';
import { Icon } from '@/components/elements/Icon';
import { IfRender } from '@/utils/jsx';

export const ContentProgressCard: FC<ContentProgressCardProps> = ({
  description,
  subtitle,
  thumbAlt,
  thumbUrl,
  title,
  containerProps,
  hideProgress = false,
  isEditable = false,
  isEditMode = false,
  isSelectedToDelete = false,
  progress = 0,
  slug = '#',
  type = 'content',
  handleDeleteItem,
  onClick,
}) => {
  const { colorMode } = useColorMode();
  const isDarkMode = colorMode === 'dark';

  return (
    <Flex
      as={Link}
      href={mountHref({ slug, isEditable, isEditMode, type })}
      onClick={(e) => {
        if (onClick) {
          e.preventDefault();
          onClick(e);
          return;
        }
        if (isEditable && isEditMode) e.stopPropagation();
      }}
      flexDir="column"
      justifyContent="space-between"
      w="full"
      maxW="21.875rem"
      h="11.25rem"
      minH="9.375rem"
      p="l"
      bg="background.secondary"
      border="1px solid"
      borderColor="border.secondary"
      borderRadius="xs"
      {...containerProps}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        gap="3xs"
        w="full"
        h="full"
      >
        <Flex flexDir="column" w="calc(100% - 6.125rem)" gap="xs">
          <Text
            size="l"
            fontWeight="medium"
            color="content.brand"
            noOfLines={1}
          >
            {title}
          </Text>
          <Tooltip
            label={subtitle}
            aria-label={subtitle}
            placement="top"
            hasArrow
            openDelay={400}
            maxW="31.25rem"
            minW="6.25rem"
            whiteSpace="normal"
          >
            <Text
              size="l"
              color="content.secondary"
              noOfLines={2}
              minH="3.125rem"
            >
              {subtitle}
            </Text>
          </Tooltip>
          <IfRender condition={!!description}>
            <Text size="m" color="content.tertiary">
              {description}
            </Text>
          </IfRender>
        </Flex>
        <IfRender condition={!isEditable}>
          <Image
            src={thumbUrl}
            alt={thumbAlt}
            w="5.5rem"
            h="3.25rem"
            objectFit="cover"
            borderRadius="2xs"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = '/assets/eqi-placeholder.png';
            }}
          />
        </IfRender>
        <IfRender condition={isEditable}>
          <CardFlipHorizontal
            showBack={isEditable && isEditMode}
            minH="0.625rem"
            frontFace={
              <Box w="5.625rem" h="3.125rem">
                <Image
                  src={thumbUrl}
                  alt={thumbAlt}
                  w="5.5rem"
                  h="3.25rem"
                  objectFit="cover"
                  borderRadius="md"
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = '/assets/eqi-placeholder.png';
                  }}
                />
              </Box>
            }
            backFace={
              <Box pos="relative" w="5.625rem" h="3.125rem">
                <Button
                  variant="naked"
                  onClick={() => {
                    if (handleDeleteItem) {
                      handleDeleteItem();
                    }
                  }}
                  pos="absolute"
                  display="flex"
                  flexDir="column"
                  alignItems="center"
                  justifyContent="center"
                  w="5.625rem"
                  h="3.125rem"
                  bgColor={
                    isSelectedToDelete
                      ? 'action.active.brand'
                      : isDarkMode
                        ? 'alpha.400'
                        : 'alpha.700'
                  }
                  opacity={isSelectedToDelete ? 1 : 0.7}
                  borderRadius="base"
                  zIndex="docked"
                  _hover={{
                    bg: isSelectedToDelete
                      ? 'action.active.brand'
                      : isDarkMode
                        ? 'alpha.500'
                        : 'alpha.900',
                  }}
                >
                  <Icon
                    iconName="Trash"
                    width={24}
                    height={24}
                    color="background.alwaysLight"
                    cursor="pointer"
                  />
                </Button>
                <Image
                  src={thumbUrl}
                  alt={thumbAlt}
                  w="5.625rem"
                  h="3.125rem"
                  borderRadius="base"
                  objectFit="cover"
                />
              </Box>
            }
          />
        </IfRender>
      </Flex>
      <IfRender condition={!hideProgress}>
        <Progress
          w="full"
          value={progress}
          borderRadius="lg"
          sx={{
            h: '0.5rem',
            bg: 'transparency.light',
            '[role=progressbar]': { bg: 'action.active.brand' },
          }}
        />
      </IfRender>
    </Flex>
  );
};

const mountHref = ({
  isEditMode,
  isEditable,
  slug,
  type,
}: {
  slug: string;
  isEditable: boolean;
  isEditMode: boolean;
  type: ContentProgressCardProps['type'];
}) => {
  if (type === 'live') {
    return '';
  }
  if (!isEditable) {
    return `${PATHS.hubs.content}/${slug}`;
  }
  if (isEditMode) {
    return '#';
  }
  return slug;
};

export type ContentProgressCardProps = {
  description: string;
  subtitle: Content['summary'];
  thumbAlt: string;
  thumbUrl: Content['thumb'];
  title: Content['title'];
  containerProps?: FlexProps;
  hideProgress?: boolean;
  isEditable?: boolean;
  isEditMode?: boolean;
  isSelectedToDelete?: boolean;
  progress?: number;
  slug?: Content['slug'];
  type?: 'content' | 'live';
  handleDeleteItem?: () => void;
  onClick?: (e: MouseEvent) => void;
};
